/**
 * FloatingHand — fan-shaped hand floating at the outer edge of the screen.
 *
 * Behavior:
 *   - "tucked" (default): cards are ~70% hidden behind the screen edge so they
 *     don't obscure the board. Only the top sliver peeks.
 *   - "raised":  toggled with the chevron button — cards lift up so the full
 *     card is visible at the screen edge.
 *
 * For the P1 (bottom) panel, the hand floats from the BOTTOM of the screen.
 * For the P2 (top)    panel, the hand floats from the TOP of the screen.
 *
 * Click a card → opens the CardActionPopup with full details + play buttons.
 * Hold ALT while hovering a card → quick-peek detail tooltip (desktop only;
 *   on mobile, click is the only interaction).
 */
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import type { Card } from "@/types/card";
import { BoardCard } from "./BoardCard";
import { cn } from "@/lib/utils";

interface FloatingHandProps {
  /** Which player's hand this is. */
  playerNum: 1 | 2;
  /** Display name shown on the toggle button. */
  playerName: string;
  /** Hand items already resolved (instanceId + Card). */
  handItems: { instanceId: string; card: Card }[];
  /** Side of the screen this hand floats from. */
  side: "top" | "bottom";
  /** Click on a hand card → parent opens the action popup. */
  onCardClick: (instanceId: string, card: Card) => void;
  /** Pass-and-play: render face-down cards instead of revealing card faces. */
  hidden?: boolean;
  /**
   * Tabletop mode: when set, the hand owner can toggle their own face-down/face-up
   * state. Tapping a face-down card calls this; a small "Hide" button calls this
   * when revealed.
   */
  onToggleSelfReveal?: () => void;
  /**
   * Tabletop mode: rotate the hand 180° so the seated top player reads it
   * right-side-up. Previously this rotation was inherited from the top panel
   * wrapper, but the hand now portal-renders to document.body to escape
   * containing-block issues, so the rotation must be passed in explicitly.
   */
  rotated?: boolean;
}

export function FloatingHand({
  playerNum: _playerNum,
  playerName,
  handItems,
  side,
  onCardClick,
  hidden = false,
  onToggleSelfReveal,
  rotated = false,
}: FloatingHandProps) {
  const [raised, setRaised] = useState(false);

  // Track ALT key globally (uses the actual altKey flag — the prior version
  // had a bug where keyup of a non-Alt key set altHeld=false incorrectly).
  const [altHeld, setAltHeld] = useState(false);
  useEffect(() => {
    const sync = (e: KeyboardEvent) => setAltHeld(e.altKey);
    const blur = () => setAltHeld(false);
    window.addEventListener("keydown", sync);
    window.addEventListener("keyup", sync);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", sync);
      window.removeEventListener("keyup", sync);
      window.removeEventListener("blur", blur);
    };
  }, []);

  // Tuck offset: about 70% of card height (the user said "3/4 hidden").
  const tuckPctClass = raised ? "translate-y-0" : side === "bottom" ? "translate-y-[68%]" : "-translate-y-[68%]";

  // P2 (top) hand sits BELOW the BoardTopBar (~2.75rem) so the bar stays
  // visible. P1 (bottom) hand sits at the very bottom of the screen.
  const edgeStyle: React.CSSProperties =
    side === "bottom"
      ? { left: 0, right: 0, bottom: 0 }
      : { left: 0, right: 0, top: "calc(env(safe-area-inset-top, 0px) + 2.75rem)" };

  // For P2 (top), raising the hand means the chevron flips to point UP (tuck).
  // For P1 (bottom), raising means the chevron flips to point DOWN (tuck).
  const chevron = (() => {
    if (side === "bottom") {
      return raised ? <ChevronDown size={14} /> : <ChevronUp size={14} />;
    }
    return raised ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  })();

  // The chip sits on the inner edge of the card row (the side that faces the
  // board): above the row for P1 (bottom), below for P2 (top). Because it's
  // inside the same translate-y'd container, it tracks the hand's raise/tuck.
  const chipWrapperClass =
    side === "bottom"
      ? "flex justify-center gap-1.5 -mb-3"
      : "flex justify-center gap-1.5 -mt-3 order-last";

  // Render to document.body via a portal so we escape any ancestor with a
  // CSS `transform` (e.g. the tabletop top-player panel which is rotated
  // 180°). A `transform` on an ancestor makes that ancestor the containing
  // block for `position: fixed` descendants, which previously caused the top
  // player's hand to float in the middle of their rotated panel instead of
  // pinning to the screen edge. The portal sidesteps this by mounting the
  // hand outside the rotated subtree.
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="pointer-events-none fixed z-40"
      style={{
        ...edgeStyle,
        ...(rotated ? { transform: "rotate(180deg)" } : null),
      }}
    >
      {/* Outer wrapper handles tuck/raise translate. Chevron + card-row are
          siblings inside so they translate together — the old version had the
          chip on the OUTER fixed (full-screen) container, leaving it stranded
          off-screen. */}
      <div
        className={cn(
          "pointer-events-auto flex w-full flex-col transition-transform duration-200 ease-out",
          tuckPctClass,
        )}
      >
        {/* Chevron toggle — positioned on the inner edge so it follows the
            hand as it tucks/raises. */}
        <div className={cn("pointer-events-auto relative z-10", chipWrapperClass)}>
          <button
            type="button"
            onClick={() => setRaised((s) => !s)}
            className="flex h-6 w-10 items-center justify-center rounded-md border border-[#2a2f3a] bg-[#1a1d24]/95 text-[#94a3b8] shadow-md hover:border-[#475569] hover:bg-[#22262f] hover:text-[#e2e8f0]"
            title={raised ? "Tuck hand" : "Raise hand"}
            aria-label={raised ? "Tuck hand" : "Raise hand"}
          >
            {chevron}
          </button>
          {onToggleSelfReveal && !hidden && (
            <button
              type="button"
              onClick={onToggleSelfReveal}
              className="flex h-6 items-center gap-1 rounded-md border border-[#2a2f3a] bg-[#1a1d24]/95 px-2 text-[10px] uppercase tracking-wider text-[#94a3b8] shadow-md hover:border-[#475569] hover:bg-[#22262f] hover:text-[#e2e8f0]"
              title="Hide your hand"
              aria-label="Hide your hand"
            >
              <EyeOff size={12} />
              Hide
            </button>
          )}
        </div>
        {/* Card row */}
        <div
          className={cn(
            "flex w-full justify-center gap-1 overflow-x-auto px-2 py-2 sm:gap-2 sm:px-4",
            side === "top" ? "items-start" : "items-end",
          )}
          style={{
            background:
              side === "bottom"
                ? "linear-gradient(to top, rgba(14,16,20,0.85) 0%, rgba(14,16,20,0.5) 70%, rgba(14,16,20,0) 100%)"
                : "linear-gradient(to bottom, rgba(14,16,20,0.85) 0%, rgba(14,16,20,0.5) 70%, rgba(14,16,20,0) 100%)",
          }}
        >
          {handItems.length === 0 && (
            <span className="px-2 py-4 text-[11px] uppercase tracking-wider text-[#64748b]">
              {playerName} hand empty
            </span>
          )}
          {hidden
            ? handItems.map(({ instanceId }) => (
                <HiddenHandCard
                  key={instanceId}
                  onReveal={onToggleSelfReveal}
                />
              ))
            : handItems.map(({ instanceId, card }) => (
                <FloatingHandCard
                  key={instanceId}
                  instanceId={instanceId}
                  card={card}
                  altHeld={altHeld}
                  menuSide={side}
                  onClick={() => onCardClick(instanceId, card)}
                />
              ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Hidden (face-down) hand card for pass-and-play ─────────────────────────

function HiddenHandCard({ onReveal }: { onReveal?: () => void }) {
  const baseClasses =
    "relative h-28 w-20 shrink-0 overflow-hidden rounded border border-[#2a2f3a] bg-gradient-to-br from-[#1a1d24] to-[#0e1014] shadow-md";

  const inner = (
    <>
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(245,158,11,0.18) 0 4px, transparent 4px 10px)",
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
        <span className="font-display text-base font-bold tracking-wider text-amber-soft/70">
          GM
        </span>
        {onReveal && (
          <span className="flex items-center gap-1 text-[8px] uppercase tracking-wider text-amber-soft/80">
            <Eye size={10} />
            Tap to reveal
          </span>
        )}
      </div>
    </>
  );

  if (onReveal) {
    return (
      <button
        type="button"
        onClick={onReveal}
        className={cn(
          baseClasses,
          "cursor-pointer transition-all hover:border-amber-soft/60 hover:ring-2 hover:ring-amber-soft/40",
        )}
        aria-label="Reveal your hand"
        title="Tap to reveal your hand"
      >
        {inner}
      </button>
    );
  }

  return (
    <div className={baseClasses} aria-label="Opponent hand card (face-down)">
      {inner}
    </div>
  );
}

// ─── A single card in the floating hand ───────────────────────────────────────

interface FloatingHandCardProps {
  instanceId: string;
  card: Card;
  altHeld: boolean;
  menuSide: "top" | "bottom";
  onClick: () => void;
}

function FloatingHandCard({
  card,
  altHeld,
  menuSide,
  onClick,
}: FloatingHandCardProps) {
  const [hover, setHover] = useState(false);
  const showDetail = hover && altHeld;

  return (
    <div
      className="relative shrink-0"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <BoardCard card={card} size="hand" onClick={onClick} />
      {showDetail && <CardDetailTooltip card={card} side={menuSide} />}
    </div>
  );
}

// ─── ALT-held quick-peek tooltip ─────────────────────────────────────────────

function CardDetailTooltip({ card, side }: { card: Card; side: "top" | "bottom" }) {
  const pos =
    side === "bottom"
      ? "bottom-full left-1/2 -translate-x-1/2 mb-2"
      : "top-full left-1/2 -translate-x-1/2 mt-2";

  const baseKw = card.keywords ?? [];
  const atkKw = card.attackKeywords ?? [];
  const defKw = card.defendKeywords ?? [];
  const weight =
    card.attackDefendFields?.weight ??
    card.signatureMoveFields?.attackWeight ??
    "";
  const attackType =
    card.attackDefendFields?.attackType ??
    card.signatureMoveFields?.attackType ??
    "";
  const defendType = card.attackDefendFields?.defendType ?? "";
  const power = card.attackDefendFields?.powerValue ?? card.signatureMoveFields?.powerValue ?? "";
  const block = card.attackDefendFields?.blockValue ?? card.signatureMoveFields?.blockValue ?? "";

  return (
    <div
      className={cn(
        "absolute z-50 w-72 rounded-lg border-2 border-amber-soft/60 bg-[#0e1014] p-3 text-left shadow-2xl shadow-black/60",
        pos,
      )}
    >
      <div className="flex gap-3">
        <div className="relative h-40 w-28 shrink-0 overflow-hidden rounded border border-[#2a2f3a] bg-[#13151a]">
          {card.imageDataUrl ? (
            <img
              src={card.imageDataUrl}
              alt={card.name}
              className="absolute inset-0 h-full w-full object-cover"
              draggable={false}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-4xl font-black text-white/15">
              {card.cardType?.[0] ?? "?"}
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-sm font-bold leading-tight text-white">{card.name}</p>
          <p className="text-[10px] uppercase tracking-wider text-[#94a3b8]">
            {card.cardType}
          </p>
          {(power || block) && (
            <div className="mt-0.5 flex gap-1">
              {power && (
                <span className="rounded bg-red-500/30 px-1.5 py-0.5 font-mono text-[10px] font-bold text-red-200">
                  PWR {power}
                </span>
              )}
              {block && (
                <span className="rounded bg-blue-500/30 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-200">
                  BLK {block}
                </span>
              )}
            </div>
          )}
          {(weight || attackType || defendType) && (
            <div className="flex flex-wrap gap-0.5">
              {weight && (
                <span className="rounded bg-amber-500/20 px-1 py-px text-[9px] uppercase tracking-wide text-amber-300">
                  {weight}
                </span>
              )}
              {attackType && (
                <span className="rounded bg-blue-500/20 px-1 py-px text-[9px] uppercase tracking-wide text-blue-300">
                  Atk: {attackType}
                </span>
              )}
              {defendType && (
                <span className="rounded bg-blue-500/20 px-1 py-px text-[9px] uppercase tracking-wide text-blue-300">
                  Def: {defendType}
                </span>
              )}
            </div>
          )}
          {baseKw.length + atkKw.length + defKw.length > 0 && (
            <div className="mt-0.5 flex flex-wrap gap-0.5">
              {[...baseKw, ...atkKw, ...defKw].map((k, i) => (
                <span
                  key={`${k}_${i}`}
                  className="rounded bg-violet-500/20 px-1 py-px text-[9px] uppercase tracking-wide text-violet-300"
                >
                  {k}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 space-y-1 border-t border-[#2a2f3a] pt-2 text-[11px] leading-snug text-[#cbd5e1]">
        {card.attackDefendFields?.attackAbility && (
          <p>
            <span className="font-semibold text-red-300">Attack: </span>
            {card.attackDefendFields.attackAbility}
          </p>
        )}
        {card.attackDefendFields?.defendAbility && (
          <p>
            <span className="font-semibold text-blue-300">Defend: </span>
            {card.attackDefendFields.defendAbility}
          </p>
        )}
        {card.signatureMoveFields?.ability && (
          <p>
            <span className="font-semibold text-violet-300">Signature: </span>
            {card.signatureMoveFields.ability}
          </p>
        )}
        {card.utilityFields?.ability && (
          <p>
            <span className="font-semibold text-emerald-300">Utility: </span>
            {card.utilityFields.ability}
          </p>
        )}
        {card.stanceFields?.ability && (
          <p>
            <span className="font-semibold text-teal-300">Stance: </span>
            {card.stanceFields.ability}
          </p>
        )}
        {card.rulesText && <p className="text-[#94a3b8]">{card.rulesText}</p>}
      </div>
    </div>
  );
}
